import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Stethoscope, ClipboardList, MessageSquare, AlertTriangle,
  Activity, TrendingUp, FileClock } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../auth";
import { useI18n } from "../../i18n";
import { StatCard, Card, Badge, Spinner, EmptyState } from "../../components/ui";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({})); }, []);

  if (!stats) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-bold text-navy text-2xl">{t("welcome")}, {user.name}</h1>
        <p className="text-slate-500 text-sm mt-1">{t(`role_${user.role}`)}{user.clinic?.name ? ` · ${user.clinic.name}` : ""}</p>
      </div>

      {user.role === "superadmin" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label={t("stat_clinics")} value={stats.clinics_active} icon={Building2} tone="teal" />
            <StatCard label={t("stat_accounts")} value={stats.accounts} icon={Users} tone="navy" />
            <StatCard label={t("stat_pros")} value={stats.professionals} icon={Stethoscope} tone="teal" />
            <StatCard label={t("stat_active_programs")} value={stats.programs_active} icon={ClipboardList} tone="navy" />
          </div>
          <AuditList items={stats.recent_audit} t={t} />
        </>
      )}

      {user.role === "clinic_admin" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label={t("stat_pros")} value={stats.professionals} icon={Stethoscope} tone="teal" />
            <StatCard label={t("patients")} value={stats.patients} icon={Users} tone="navy" />
            <StatCard label={t("stat_active_programs")} value={stats.programs_active} icon={ClipboardList} tone="teal" />
            <StatCard label={t("to_reeval")} value={stats.programs_reeval} icon={FileClock} tone="warn" />
          </div>
          <AuditList items={stats.recent_audit} t={t} />
        </>
      )}

      {(user.role === "professional" || user.role === "assistant") && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label={t("my_patients")} value={stats.patients_count || 0} icon={Users} tone="teal" />
            <StatCard label={t("to_reeval")} value={(stats.programs_reeval || []).length} icon={FileClock} tone="warn" />
            <StatCard label={t("high_pain_alert")} value={(stats.high_pain || []).length} icon={AlertTriangle} tone="alert" />
            <StatCard label={t("unread")} value={stats.unread_messages || 0} icon={MessageSquare} tone="navy" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <Card className="p-5">
              <h3 className="font-head font-semibold text-navy mb-3 flex items-center gap-2"><Users size={18} className="text-teal" /> {t("my_patients")}</h3>
              {(stats.patients || []).length === 0 ? <EmptyState title={t("no_data")} /> : (
                <div className="divide-y divide-slate-100">
                  {stats.patients.map((p) => (
                    <Link key={p.id} to={`/app/patients/${p.id}`} className="flex items-center justify-between py-2.5 hover:bg-vlg -mx-2 px-2 rounded-lg">
                      <span className="text-sm font-medium text-navy">{p.first_name} {p.last_name}</span>
                      <Badge tone="navy">{p.internal_number}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5">
              <h3 className="font-head font-semibold text-navy mb-3 flex items-center gap-2"><AlertTriangle size={18} className="text-warn" /> Indicateurs</h3>
              <div className="space-y-2">
                {(stats.high_pain || []).slice(0, 4).map((h) => (
                  <div key={h.id} className="flex items-center gap-2 text-sm bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <TrendingUp size={15} className="text-alert" /> <span className="text-alert font-medium">{t("high_pain_alert")}</span>
                    <span className="ms-auto text-alert font-bold">{h.pain_after}/10</span>
                  </div>
                ))}
                {(stats.feedback || []).slice(0, 3).map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-sm bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                    <MessageSquare size={15} className="text-warn" /> <span className="text-slate-600 truncate">{f.comment}</span>
                  </div>
                ))}
                {stats.hill_dt_enabled && (stats.hilldt_recent || []).slice(0, 2).map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
                    <Activity size={15} className="text-teal" /> <span className="text-slate-600">Séance Hill DT — {s.region}</span>
                  </div>
                ))}
                {((stats.high_pain || []).length + (stats.feedback || []).length) === 0 && <EmptyState title={t("no_data")} />}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function AuditList({ items, t }) {
  return (
    <Card className="p-5">
      <h3 className="font-head font-semibold text-navy mb-3 flex items-center gap-2"><FileClock size={18} className="text-teal" /> {t("recent_activity")}</h3>
      {(!items || items.length === 0) ? <EmptyState title={t("no_data")} /> : (
        <div className="divide-y divide-slate-100">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 py-2 text-sm">
              <Badge tone="teal">{a.action}</Badge>
              <span className="text-slate-500 truncate">{a.user_email}</span>
              <span className="ms-auto text-xs text-slate-400">{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
